import {
  AcceptAgreementFeature,
  Avatar_v2 as Avatar,
  Button_v2 as Button,
  ModalSheet,
  Typography,
  TypographyVariantsEnum,
} from '@mindly/ui-components';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { ConfirmAppointmentFeatureProps, ConfirmAppointmentFeatureRef } from './types';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { ContractWithSpecialist } from '../../components/ContractWithSpecialist';
import { ContractWithSpecialistRef } from '../../components/ContractWithSpecialist/ContractWithSpecialist';
import { FormattedMessage } from 'react-intl';
import { useAppointment } from 'src/hooks/useAppointment/useAppointment';
import { useIsTrialSession } from 'src/shared/hooks/useIsTrialSession';
import { PrivateRoutes } from 'src/routes/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useAnalytics } from 'src/shared';
import { specialistSelectorPositionById } from 'src/redux/specialists/selector';
import { LoadPaymentModal } from 'src/modals/LoadPaymentModal';
import { useIonRouter, useIonViewWillLeave } from '@ionic/react';
import { fetchSpecialistByID } from 'src/redux/loadedSpecialists/thunk';
import { OverlayEventDetail } from '@ionic/react/dist/types/components/react-component-lib/interfaces';

const ConfirmAppointmentFeature = forwardRef<ConfirmAppointmentFeatureRef, ConfirmAppointmentFeatureProps>(
  ({ specialist, selectedSlot, setIsBookSlotLoading, onClose }, ref) => {
    const { t, i18n } = useTranslation('app');
    const contractRef = useRef<ContractWithSpecialistRef>(null);
    const [isAccept, setIsAccept] = useState(false);
    const [isApplyContractLoading, setIsApplyContractLoading] = useState(false);
    const {
      bookingActionHandler,
      pushToAppointmentPage,
      bookingActionLoadingStatus,
      paymentStatus,
      clearStates,
      checkIsSubscriptionAvailable,
      setBookingActionLoadingStatus,
    } = useAppointment();
    const { checkIsTrialSession } = useIsTrialSession(specialist.id);
    const [isOpen, setIsOpen] = useState(false);
    const history = useHistory();
    const dispatch = useDispatch();
    const { trackPredefinedEvent } = useAnalytics();
    const specialistPosition = useSelector(specialistSelectorPositionById(specialist.id));
    const isTrialSessionRef = useRef<boolean>(false);
    const specialistShortData = useMemo(() => {
      return {
        id: specialist.id,
        name: `${specialist.first_name} ${specialist.last_name}`,
        order_position: specialistPosition,
      };
    }, [specialist, specialistPosition]);
    const loaderModalRef = useRef<HTMLIonModalElement | null>(null);
    const navigation = useIonRouter();

    const handleModalViewed = useCallback(() => {
      if (!selectedSlot) return;
      trackPredefinedEvent('BOOKING_CONFIRMATION_MODAL_VIEWED', {
        ...specialistShortData,
        date_chosen: DateTime.fromISO(selectedSlot).toFormat('yyyy-MM-dd HH:mm'),
      });
    }, [trackPredefinedEvent, specialistShortData, selectedSlot]);

    const bookingConfirmationClicked = useCallback(() => {
      if (!selectedSlot) return;
      trackPredefinedEvent('BOOKING_CONFIRMATION_MODAL_CLICKED', {
        ...specialistShortData,
        date_chosen: DateTime.fromISO(selectedSlot).toFormat('yyyy-MM-dd HH:mm'),
      });
    }, [trackPredefinedEvent, specialistShortData, selectedSlot]);

    useEffect(() => {
      if (bookingActionLoadingStatus === 'success') {
        dispatch(fetchSpecialistByID(specialist.id));
        loaderModalRef.current?.dismiss();
        // none to prevent animation
        navigation.push(PrivateRoutes.CONSULTATION, 'none');
      }
    }, [bookingActionLoadingStatus, specialist.id, dispatch, navigation]);

    useIonViewWillLeave(() => {
      clearStates();
      loaderModalRef.current = null;
      isTrialSessionRef.current = false;
    }, []);

    useImperativeHandle(ref, () => ({
      handleBookingClick,
    }));

    const handleClose = useCallback(
      (e?: CustomEvent<OverlayEventDetail>) => {
        setIsOpen(false);
        // if e.detail.role is not exists it's means that the modal will closed manually
        if (!e?.detail?.role) return;
        onClose?.();
        isTrialSessionRef.current = false;
        setBookingActionLoadingStatus(null);
      },
      [onClose, setBookingActionLoadingStatus, specialist.id]
    );

    const bookSlot = useCallback(async () => {
      setIsBookSlotLoading?.(true);
      try {
        if (!selectedSlot) return;
        await bookingActionHandler(specialist.id, selectedSlot, true);
        handleClose({ detail: { role: 'manual' } } as CustomEvent<OverlayEventDetail>);
      } catch (error) {
        console.error(error);
      } finally {
        setIsBookSlotLoading?.(false);
      }
    }, [bookingActionHandler, selectedSlot, specialist.id, setIsBookSlotLoading, handleClose]);

    const handleApplyContract = useCallback(async () => {
      setIsApplyContractLoading(true);
      try {
        await contractRef.current?.applyContract();
      } catch (error) {
        console.error(error);
      } finally {
        setIsApplyContractLoading(false);
      }
    }, []);

    const handleAgreementAccept = useCallback(() => {
      setIsAccept(true);
      bookSlot();
      bookingConfirmationClicked();
      setIsOpen(false);
    }, [bookSlot, bookingConfirmationClicked, setIsOpen]);

    const handleBookingClick = useCallback(async () => {
      // if no slot selected alredy on profile page, we need to redirect to the appointment page like in old case
      if (!selectedSlot) {
        pushToAppointmentPage(specialist.id);
        return;
      }
      setBookingActionLoadingStatus('start');
      const isTrialSession = await checkIsTrialSession();
      isTrialSessionRef.current = isTrialSession;
      // if trial session, we need to show the agreement modal
      if (isTrialSession) {
        setIsOpen(true);
        handleModalViewed();
        return;
      }

      bookSlot();
    }, [
      checkIsTrialSession,
      specialist.id,
      bookingActionHandler,
      pushToAppointmentPage,
      handleModalViewed,
      setBookingActionLoadingStatus,
    ]);

    return (
      <>
        <ContractWithSpecialist
          ref={contractRef}
          specialistId={specialist.id}
          onAgreementAccepted={handleAgreementAccept}
        />
        <ModalSheet isOpen={isOpen} heightInPx={420} onDidDismiss={handleClose}>
          <div className="px-5 pt-8 pb-[var(--ion-safe-area-bottom)] flex flex-col justify-between h-full">
            <div className="flex flex-col items-center">
              <Avatar src={specialist?.avatar_link!} variant="large" round="drop" />
              <Typography variant={TypographyVariantsEnum.Title20Semi} className="mt-4 mb-1 text-center">
                {t('book_session_with')} {specialist?.first_name} {specialist?.last_name?.slice(0, 1)}.<br />
                {selectedSlot &&
                  DateTime.fromISO(selectedSlot).toFormat('dd MMMM yyyy, HH:mm', {
                    locale: i18n.language,
                  })}
              </Typography>
              <Typography
                variant={TypographyVariantsEnum.Text14Regular}
                className="text-typography-typography-secondary mb-4"
              >
                {DateTime.local().zoneName} (GTM {DateTime.local().zone.formatOffset(Date.now(), 'short')})
              </Typography>
              <div className="pr-6">
                <AcceptAgreementFeature
                  handlePressLink={() => contractRef.current?.openContract()}
                  translations={[
                    t('app:confirm_agreement.text.0'),
                    t('app:confirm_agreement.text.1'),
                    t('app:confirm_agreement.text.2'),
                  ]}
                  isAccepted={isAccept}
                  setIsAccepted={setIsAccept}
                />
              </div>
            </div>
            <Button
              fullWidth
              data-testid="appointment-button"
              disabled={!isAccept}
              isLoading={isApplyContractLoading}
              size="large"
              onClick={handleApplyContract}
              className="!mb-5"
            >
              <FormattedMessage id="specialist.profile.signIn" defaultMessage="Записатися" />
            </Button>
          </div>
        </ModalSheet>
        <LoadPaymentModal
          ref={loaderModalRef}
          fetchingStatus={bookingActionLoadingStatus}
          paymentStatus={paymentStatus}
          loadType={checkIsSubscriptionAvailable(specialist.id) ? 'subscriptionBooking' : 'go_to_payment'}
          isTrial={isTrialSessionRef.current}
          history={history}
        />
      </>
    );
  }
);

export default ConfirmAppointmentFeature;
